// Single source of truth for lead intents across all Zamzi verticals.
// Keep this list in sync with the forms that submit leads and the owner
// dashboard/follow-up logic that consumes them.
export const LEAD_INTENTS = [
  // Universal / legacy
  'general-inquiry',
  'booking-request',
  'instant-book',
  // Zamzi Stay
  'stay-booking-inquiry',
  // Craving (restaurants)
  'restaurant-booking-inquiry',
  // Shopaholic (retail / sales)
  'retail-inquiry',
  // FixItSquad
  'fixit-inquiry',
  // Job seekers (LinkedIn-style hiring)
  'jobseeker-general-inquiry',
  'jobseeker-availability-check',
  'jobseeker-cv-request',
  'jobseeker-interview-invite',
  'jobseeker-reference-request',
] as const;

export type LeadIntent = (typeof LEAD_INTENTS)[number];

export type LeadVertical = 'universal' | 'stays' | 'craving' | 'shopaholic' | 'fixit' | 'jobseeker';

export type LeadIntentMeta = {
  label: string;
  helper: string;
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  vertical: LeadVertical;
};

export const LEAD_INTENT_META: Record<LeadIntent, LeadIntentMeta> = {
  'general-inquiry': {
    label: 'Inquiry',
    helper: 'General inquiry from your public profile.',
    variant: 'outline',
    vertical: 'universal',
  },
  'booking-request': {
    label: 'Booking Request',
    helper: 'Manual booking request awaiting your response.',
    variant: 'secondary',
    vertical: 'universal',
  },
  'instant-book': {
    label: 'Instant Book',
    helper: 'Booking captured via Instant Book widget.',
    variant: 'default',
    vertical: 'universal',
  },
  'stay-booking-inquiry': {
    label: 'Stay Enquiry',
    helper: 'Guest inquiry about dates, rates or availability.',
    variant: 'secondary',
    vertical: 'stays',
  },
  'restaurant-booking-inquiry': {
    label: 'Table Enquiry',
    helper: 'Diner inquiry about a table, menu or availability.',
    variant: 'secondary',
    vertical: 'craving',
  },
  'retail-inquiry': {
    label: 'Product Inquiry',
    helper: 'Shopper inquiry about a product, order or pricing.',
    variant: 'secondary',
    vertical: 'shopaholic',
  },
  'fixit-inquiry': {
    label: 'Job Enquiry',
    helper: 'Customer inquiry about a job, quote or call-out fee.',
    variant: 'secondary',
    vertical: 'fixit',
  },
  'jobseeker-general-inquiry': {
    label: 'Hiring Enquiry',
    helper: 'Employer inquiry about the candidate.',
    variant: 'outline',
    vertical: 'jobseeker',
  },
  'jobseeker-availability-check': {
    label: 'Availability Check',
    helper: 'Employer checking availability or notice period.',
    variant: 'outline',
    vertical: 'jobseeker',
  },
  'jobseeker-cv-request': {
    label: 'CV Request',
    helper: "Employer requested the candidate's CV.",
    variant: 'default',
    vertical: 'jobseeker',
  },
  'jobseeker-interview-invite': {
    label: 'Interview Invite',
    helper: 'Employer invited the candidate to interview.',
    variant: 'default',
    vertical: 'jobseeker',
  },
  'jobseeker-reference-request': {
    label: 'Reference Request',
    helper: 'Employer requested references from the candidate.',
    variant: 'outline',
    vertical: 'jobseeker',
  },
};

export const isLeadIntent = (value: unknown): value is LeadIntent =>
  typeof value === 'string' && (LEAD_INTENTS as readonly string[]).includes(value);

// Intents that represent a genuine booking/order attempt (as opposed to a
// general inquiry). Used for owner reminders and booking-request analytics so
// general questions don't inflate the public bookingRequests counter.
export const BOOKING_LEAD_INTENTS: readonly LeadIntent[] = [
  'booking-request',
  'stay-booking-inquiry',
  'restaurant-booking-inquiry',
];

export const isBookingIntent = (value: unknown): value is LeadIntent =>
  typeof value === 'string' && (BOOKING_LEAD_INTENTS as readonly string[]).includes(value);

/**
 * Intents an owner can **accept or decline** from the Leads tab.
 *
 * This is deliberately broader than {@link isBookingIntent}, and the two must not
 * be merged. They answer different questions:
 *
 *   - `isBookingIntent` — "is there a reservation record behind this?" Drives
 *     booking analytics and the `confirmReservation` handoff.
 *   - `isDecisionIntent` — "does the owner make a yes/no call on this?" Drives
 *     the Accept / Decline buttons and the Pending / Accepted / Declined counts.
 *
 * A Shopaholic product enquiry is decisionable but is NOT a booking: a seller
 * accepts "do you have this in stock?" with no reservation anywhere. Gating the
 * decision on `isBookingIntent` is what left retail sellers unable to accept or
 * decline anything, while the tab still advertised the Pending / Accepted /
 * Declined filters — every one of which silently read 0, so clicking one showed
 * "No inquiries found in this filter" while the seller's leads sat in All.
 *
 * Deliberately excluded:
 *   - `fixit-inquiry` — the Fix-it crew confirms a job from the link in the SMS
 *     alert, and `MyInquiries` renders that card as informational only.
 *   - `general-inquiry` and the `jobseeker-*` intents — a question with nothing
 *     to decide, or handled by an entirely different surface.
 */
export const DECISION_LEAD_INTENTS: readonly LeadIntent[] = [
  'booking-request',
  'stay-booking-inquiry',
  'restaurant-booking-inquiry',
  'retail-inquiry',
];

export const isDecisionIntent = (value: unknown): value is LeadIntent =>
  typeof value === 'string' && (DECISION_LEAD_INTENTS as readonly string[]).includes(value);

/**
 * Intents that may drive a **reservation handoff** — matching the lead to a
 * pending booking and calling `confirmReservation`.
 *
 * This is the narrowest of the three predicates, and it exists because the other
 * two are both too wide for a money path:
 *
 *   - `isBookingIntent` includes `stay-booking-inquiry` and
 *     `restaurant-booking-inquiry`, because those count towards the public
 *     "booking requests" analytics and trigger owner follow-up reminders. Both of
 *     those uses are about *interest*, not about a confirmed stay.
 *   - `isDecisionIntent` includes every lead an owner can answer, including
 *     `retail-inquiry`, which is never a booking at all.
 *
 * Only `booking-request` ("Manual booking request awaiting your response")
 * represents a guest who has actually asked for a reservation. The three
 * vertical intents are questions from the *same* public enquiry form
 * (`profile/[userId]/page.tsx`): "what are your December rates?" is not a
 * booking, and confirming one would send a payment request to a guest who never
 * asked to book.
 *
 * `instant-book` is absent deliberately — it is already a confirmed booking and
 * never arrives through the accept path.
 */
export const RESERVATION_LEAD_INTENTS: readonly LeadIntent[] = ['booking-request'];

export const isReservationIntent = (value: unknown): value is LeadIntent =>
  typeof value === 'string' && (RESERVATION_LEAD_INTENTS as readonly string[]).includes(value);

export const getLeadIntentMeta = (intent?: string | null): LeadIntentMeta | null =>
  isLeadIntent(intent) ? LEAD_INTENT_META[intent] : null;
